/**
 * Pure helpers for the task screens: status meaning, labels, URL state. No React here so it
 * can be unit tested directly.
 */

import { isDriveLink } from './driveLink';

// Every status has an icon and a label; color is never the only signal.
export const STATUS_META = {
    BLOCKED: { label: 'Blocked', icon: 'block', color: 'error' },
    OVERDUE: { label: 'Overdue', icon: 'warning', color: 'error' },
    DUE_SOON: { label: 'Due soon', icon: 'alarm', color: 'warning' },
    IN_PROGRESS: { label: 'In progress', icon: 'play', color: 'info' },
    ASSIGNED: { label: 'To do', icon: 'dot', color: 'primary' },
    UNASSIGNED: { label: 'Unassigned', icon: 'person_off', color: 'warning' },
    UP_NEXT: { label: 'Up next', icon: 'next', color: 'default' },
    ON_HOLD: { label: 'On hold', icon: 'pause', color: 'default' },
    WAITING: { label: 'Waiting', icon: 'hourglass', color: 'default' },
    READY_TO_START: { label: 'Ready to start', icon: 'play', color: 'primary' },
    DONE: { label: 'Done', icon: 'check', color: 'success' },
    CANCELLED: { label: 'Cancelled', icon: 'cancel', color: 'default' },
};

/** The one status a row shows, most urgent first. */
export const displayStatus = (row) => {
    if (!row) return null;
    if (row.extras?.upNext) return 'UP_NEXT';
    if (row.overallStatus === 'COMPLETED') return 'DONE';
    if (row.overallStatus === 'CANCELLED') return 'CANCELLED';
    if (row.currentStageStatus === 'READY_TO_START') return 'READY_TO_START';
    if (row.waitingForFile) return 'WAITING';
    if (row.overallStatus === 'ON_HOLD') return 'ON_HOLD';
    if (row.currentStageStatus === 'BLOCKED') return 'BLOCKED';
    if (row.overdue) return 'OVERDUE';
    if (row.redacted) return row.currentStageStatus === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'ASSIGNED';
    if (!row.currentStageAssignee && row.currentStageKey) return 'UNASSIGNED';
    if (row.dueSoon) return 'DUE_SOON';
    if (row.currentStageStatus === 'IN_PROGRESS') return 'IN_PROGRESS';
    return 'ASSIGNED';
};

export const ATTENTION_LABELS = {
    FLAGGED: 'Flagged',
    REGRESSED: 'Stage went backward in editor',
    EDITOR_AHEAD: 'Editor ahead of task',
    LANGUAGE_REMOVED: 'Language removed',
    UNASSIGNED: 'Unassigned',
    CONTENT_DELETED: 'File deleted',
    SYNC_FAILED: 'Sync failed',
    EDITOR_SUBMIT_FAILED: "Couldn't submit in the editor",
    OVERDUE: 'Overdue',
    BLOCKED: 'Blocked',
    FILE_NEEDED: 'File needed',
    VIDEO_FAILED: 'Video failed',
};

/** What a waiting task of this type is waiting for, in the middle of a sentence. */
export const fileWord = (contentType) => ({ VIDEO: 'video', TEXT: 'text file', BOOK: 'book', AUDIO: 'audio' })[contentType] || 'file';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// row: gives FILE_NEEDED its file and date
export const attentionLabel = (reason, extras, row) => {
    if (reason === 'FILE_NEEDED' && row?.fileNeededBy) {
        const what = row.currentStageStatus === 'READY_TO_START' ? 'Start video translation' : capitalize(fileWord(row.contentType));
        return `${what} needed by ${shortDate(row.fileNeededBy)}`;
    }
    if (reason === 'BLOCKED' && extras?.blockedDays != null) {
        const n = extras.blockedDays;
        return `Blocked ${n} day${n === 1 ? '' : 's'}`;
    }
    return ATTENTION_LABELS[reason] || reason;
};

export const SEND_BACK_REASONS = [
    { value: 'MISSING_FOOTAGE', label: 'Missing footage' },
    { value: 'RESHOOT', label: 'Re-shoot' },
    { value: 'QUALITY', label: 'Quality' },
    { value: 'WRONG_SOURCE', label: 'Wrong source' },
    { value: 'OTHER', label: 'Other' },
];

// PM send-backs on posts; QC's and Text QC's take only a comment
export const POST_SEND_BACK_REASONS = [
    { value: 'TEXT_ERROR', label: 'Text error' },
    { value: 'DESIGN_CHANGE', label: 'Design change' },
    { value: 'OTHER', label: 'Other' },
];

export const BLOCK_REASONS = [
    { value: 'WAITING_ON_SOURCE', label: 'Waiting on source' },
    { value: 'WAITING_ON_PM', label: 'Waiting on PM' },
    { value: 'WAITING_ON_OTHER_STAGE', label: 'Waiting on another stage' },
    { value: 'OTHER', label: 'Other' },
];

export const reasonLabel = (list, value) => list.find((r) => r.value === value)?.label || value;

export const CONTENT_TYPE_LABELS = { VIDEO: 'Video', TEXT: 'Text', BOOK: 'Book', POST: 'Post', AUDIO: 'Dubbing audio' };

/** Short name for an email: "jane_doe@x.org" becomes "jane doe". */
export const shortName = (email) => {
    if (!email) return '';
    return email.split('@')[0].replace(/[._]+/g, ' ');
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-21" as "21 Oct", with the year when it isn't this year. */
export const shortDate = (isoDate, today = new Date()) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-').map(Number);
    return `${d} ${MONTHS[m - 1]}${y === today.getFullYear() ? '' : ` ${y}`}`;
};

/** "2026-10-21" as "21 Oct", or "today" / "tomorrow" / "yesterday" relative to today. */
export const formatDue = (isoDate, today = new Date()) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-').map(Number);
    const due = Date.UTC(y, m - 1, d);
    const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.round((due - now) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days === -1) return 'yesterday';
    return shortDate(isoDate, today);
};

// Keys kept in the /tasks URL so views are bookmarkable and emails can deep link. Filters may
// hold comma lists (rev6 Part 4); posting and due are date filters; saved is the open saved
// filter; on is what the calendar places by.
const URL_KEYS = ['view', 'saved', 'task', 'layout', 'on', 'group', 'sort', 'direction', 'stage', 'assignee', 'contentType', 'language', 'status', 'pm', 'category', 'platform', 'posting', 'due', 'q', 'allPms', 'upcoming', 'user', 'page', 'date'];

export const parseTaskUrl = (search) => {
    const params = new URLSearchParams(search);
    const state = {};
    URL_KEYS.forEach((k) => {
        const v = params.get(k);
        if (v !== null && v !== '') state[k] = v;
    });
    if (state.page !== undefined) state.page = Math.max(0, parseInt(state.page, 10) || 0);
    if (state.allPms !== undefined) state.allPms = state.allPms === 'true';
    if (state.upcoming !== undefined) state.upcoming = state.upcoming === 'true';
    return state;
};

export const buildTaskUrl = (state) => {
    const params = new URLSearchParams();
    URL_KEYS.forEach((k) => {
        const v = state[k];
        if (v === undefined || v === null || v === '' || v === false) return;
        if (k === 'page' && Number(v) === 0) return;
        params.set(k, String(v));
    });
    const s = params.toString();
    return s ? `?${s}` : '';
};

/** The filter object a view request takes, from URL state. */
export const filterFromUrl = (state) => {
    const { view, task, layout, saved, on, ...filter } = state;
    return filter;
};

/**
 * An up-next row is about a stage the task hasn't reached yet: these give that stage, its
 * planned assignee and due date, or the current stage's for any other row.
 */
export const rowStageKey = (row) => (row?.extras?.upNext ? row.extras.upNextStageKey : row?.currentStageKey);
export const rowAssignee = (row) => (row?.extras?.upNext ? row.extras.upNextAssignee : row?.currentStageAssignee);
export const rowDue = (row) => (row?.extras?.upNext ? row.extras.upNextDueDate : row?.dueDate);

/** A row's React key: a task has one current row and one up-next row per stage it hasn't reached. */
export const rowKey = (row) => `${row.id}:${row.extras?.upNext ? `next:${row.extras.upNextStageKey}` : 'now'}`;

/** "· 2 up next" style suffix for a group header; empty when nothing is coming. */
export const upcomingLabel = (group) => (group?.upcoming > 0 ? `${group.upcoming} up next` : '');

/** Groups rows by extras.group, keeping server order; returns [{ group, rows }]. */
export const groupRows = (rows, groups) => {
    if (!groups || groups.length === 0) return [{ group: null, rows }];
    const byKey = new Map();
    rows.forEach((r) => {
        const key = r.extras?.group ?? null;
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(r);
    });
    return groups
        .filter((g) => byKey.has(g.key ?? null))
        .map((g) => ({ group: g, rows: byKey.get(g.key ?? null) }));
};

/** A video opens in the editor once its English is ready and it has a compressed copy. */
export const videoCanOpen = (video) => {
    const english = (video?.videoLanguages || []).find((l) => l.languageName === 'English');
    return !!english && english.status === 'READY' && !!video.compressedUrl;
};

// FAILED alone isn't enough: a later job on a working video, like adding a language, can set it too
export const videoFailed = (video) => video?.processingStatus === 'FAILED' && !videoCanOpen(video);

/** What to tell someone who tries to open a video that can't open. Only Start video can retry. */
export const videoNotReadyMessage = (video) => {
    if (!videoFailed(video)) {
        return {
            title: 'Video not ready',
            text: 'The video is not ready for editing yet. Please check back later.',
            icon: 'warning',
        };
    }
    return {
        title: 'Video failed',
        text: video.workFileEnv
            ? 'This video failed to process. Open its task and choose Start video translation again.'
            : 'This video failed to process. Delete it and upload it again.',
        icon: 'error',
    };
};

/** What moving these files to Trash does to their open tasks, added to the question. '' if nothing. */
export const trashTasksNote = (items, taskIndex) => {
    const linked = items.filter((i) => i.type === 'video' && Object.keys(taskIndex?.[i.data] || {}).length > 0);
    if (linked.length === 0) return '';
    const who =
        items.length === 1
            ? 'This video has tasks.'
            : `${linked.length} of these videos ${linked.length === 1 ? 'has' : 'have'} tasks.`;
    const retry = linked.some((i) => videoFailed(i) && i.workFileEnv)
        ? ' To try a failed video again, use Start video translation again in its task instead.'
        : '';
    return ` ${who} Tasks stay hidden until the video is restored, and are cancelled if the Trash empties first.${retry}`;
};

/** Which tasks belong to which language chip: { contentId: { languageKey: row } }. */
export const indexTasksByContent = (rows) => {
    const index = {};
    (rows || []).forEach((r) => {
        index[r.contentId] = index[r.contentId] || {};
        index[r.contentId][r.languageKey] = r;
    });
    return index;
};

/** The L2 translator of any open language of this file gets Manage task too. */
export const savesSrt = (index, contentId) => Object.values(index?.[contentId] || {}).some((r) => r.extras?.savesSrt);

// Language names as used by the editors mapped to task language keys (ISO 639-2 like the backend)
const LANGUAGE_CODES = {
    english: 'eng', gujarati: 'guj', hindi: 'hin', spanish: 'spa', french: 'fre', german: 'ger', russian: 'rus',
    mandarin: 'chi', portuguese: 'por', korean: 'kor', kannada: 'kan', telugu: 'tel', tamil: 'tam', bengali: 'ben',
    marathi: 'mar', arabic: 'ara', malayalam: 'mal', italian: 'ita', dutch: 'dut', polish: 'pol', turkish: 'tur',
    greek: 'gre', japanese: 'jpn', thai: 'tha', vietnamese: 'vie', indonesian: 'ind', malay: 'may', burmese: 'bur',
    persian: 'per', hebrew: 'heb', swahili: 'swa', hausa: 'hau', urdu: 'urd', punjabi: 'pun', sinhala: 'sin',
    nepali: 'nep', tagalog: 'tag', khmer: 'khm', lao: 'lao', swedish: 'swe', norwegian: 'nor', danish: 'dan',
    finnish: 'fin', hungarian: 'hun', czech: 'cze', romanian: 'rum', yoruba: 'yor', igbo: 'ibo', amharic: 'amh',
    somali: 'som', xhosa: 'xho', zulu: 'zul', quechua: 'que', catalan: 'cat', uzbek: 'uzb', kazakh: 'kaz',
    mongolian: 'mon', tibetan: 'tib', armenian: 'arm', georgian: 'geo', slovenian: 'slv',
};
const CODES = new Set(Object.values(LANGUAGE_CODES));

/** Mirrors the backend LanguageKeys.normalize: "Hindi-DubSync" becomes "hin-dubsync". */
export const languageKey = (raw) => {
    if (!raw) return null;
    let value = String(raw).trim();
    let suffix = '';
    const lower = value.toLowerCase();
    if (lower.endsWith('-dubsync')) suffix = '-dubsync';
    else if (lower.endsWith('-vo')) suffix = '-vo';
    const base = value.slice(0, value.length - suffix.length).toLowerCase();
    const code = LANGUAGE_CODES[base] || (CODES.has(base) ? base : null);
    return code ? code + suffix : null;
};

/** Language names for pickers, in the editors' spelling ("Hindi"). */
export const LANGUAGE_NAMES = Object.keys(LANGUAGE_CODES).map((n) => n.charAt(0).toUpperCase() + n.slice(1));

/**
 * Board columns: every active stage across the task types (only those for contentType when
 * given), merged in workflow order. A stage a row is in but no type lists still gets a column.
 */
/**
 * Board columns in workflow order. A column is a stage key plus its name: video and text call
 * TRANSLATION_R1 "Translation L1" while books call it "Translation round 1", so books get their
 * own column instead of landing under an L1 column. id is what cards and drops use.
 */
export const boardColumns = (taskTypes, rows = [], contentType) => {
    const cols = [];
    const column = (stageKey, displayName) => ({ id: columnId(stageKey, displayName), stageKey, displayName });
    (taskTypes || [])
        .filter((t) => !contentType || t.contentType === contentType)
        .forEach((t) => {
            let insertAt = 0;
            (t.stages || [])
                .filter((s) => !s.retired)
                .sort((a, b) => a.order - b.order)
                .forEach((s) => {
                    const id = columnId(s.stageKey, s.displayName);
                    const idx = cols.findIndex((c) => c.id === id);
                    if (idx >= 0) {
                        insertAt = idx + 1;
                        return;
                    }
                    // Same key under another name: sit next to it
                    const sibling = cols.findIndex((c) => c.stageKey === s.stageKey);
                    if (sibling >= 0) insertAt = sibling + 1;
                    cols.splice(insertAt, 0, column(s.stageKey, s.displayName));
                    insertAt += 1;
                });
        });
    rows.forEach((r) => {
        const key = rowStageKey(r);
        const id = rowColumnId(r);
        // key is empty on a waiting task, which has its own column
        if (key && !cols.some((c) => c.id === id)) {
            cols.push(column(key, rowStageName(r) || key));
        }
    });
    return rows.some((r) => r.waitingForFile) ? [WAITING_COLUMN, ...cols] : cols;
};

export const columnId = (stageKey, displayName) => `${stageKey}|${displayName || stageKey}`;
// Comes first, and takes no drops: a task leaves it by being linked. A null key finds the server's count.
export const WAITING_COLUMN = { id: 'WAITING', stageKey: null, displayName: 'Waiting' };
const rowStageName = (row) => (row?.extras?.upNext ? row.extras.upNextStageName : row?.currentStageName);
/** The board column a row belongs in. */
export const rowColumnId = (row) =>
    row?.waitingForFile ? WAITING_COLUMN.id : columnId(rowStageKey(row), rowStageName(row) || rowStageKey(row));

/** taskTypeKey → Set of its active stage keys and column ids, to tell where a card may be dropped. */
export const stageKeysByType = (taskTypes) =>
    Object.fromEntries(
        (taskTypes || []).map((t) => {
            const active = (t.stages || []).filter((s) => !s.retired);
            return [t.key, new Set([...active.map((s) => s.stageKey), ...active.map((s) => columnId(s.stageKey, s.displayName))])];
        }),
    );

/**
 * The languages of a post at this stage that are this person's, for Complete for several languages.
 * A PM can open someone else's language: that one has no list, or its Complete would skip it.
 */
export const sameStageLanguages = (post, stageKey, taskId) => {
    const mine = (post?.strip || []).filter(
        (e) => e.mine && e.canOpen && e.stageKey === stageKey && e.state !== 'DONE' && e.state !== 'CANCELLED',
    );
    return mine.some((e) => e.taskId === taskId) ? mine : [];
};

export const listNames = (names) => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/** Who each picked language goes to: strip entries, in one line per person. */
export const handoffLines = (picked) => {
    const byPerson = new Map();
    picked.forEach((e) => {
        const who = e.nextAssignee || '';
        byPerson.set(who, [...(byPerson.get(who) || []), e.languageLabel]);
    });
    return [...byPerson].map(([who, names]) => {
        const many = names.length > 1;
        return who
            ? `${listNames(names)} ${many ? 'go' : 'goes'} to ${shortName(who)}.`
            : `${listNames(names)} ${many ? 'wait' : 'waits'} for a person.`;
    });
};

/** A done language AV may already have taken: its posting date is today or tomorrow. */
export const postingSoon = (targetDate) => {
    if (!targetDate) return false;
    const days = (new Date(`${targetDate}T00:00:00`) - new Date(new Date().toDateString())) / 86400000;
    return days >= 0 && days <= 1;
};

/** One line after Link: { linked, total, failed: [{ language, message }] }, and the Link button's name. */
export const linkResultLine = ({ linked, total, failed = [] }, contentType) => {
    if (failed.length === 0) return `${total} language${total === 1 ? '' : 's'} linked.`;
    const failures = failed.map((f) => `${f.language}: ${f.message}`).join(' ');
    return `${linked} of ${total} languages linked. Use Link ${fileWord(contentType)} for the rest. ${failures}`;
};

/** "Hindi isn't on this video yet", for the waiting languages a file doesn't have. */
export const missingLanguagesLine = (waiting, fileLanguages, contentType) => {
    const missing = (waiting || []).filter((l) => !(fileLanguages || []).includes(l));
    if (missing.length === 0) return '';
    const word = fileWord(contentType);
    return missing.length === 1
        ? `${missing[0]} isn't on this ${word} yet. Its task starts and waits for the language to be added.`
        : `${listNames(missing)} aren't on this ${word} yet. Their tasks start and wait for the languages to be added.`;
};

/** The drawer's next step for a waiting text file or book. */
export const waitingFileLine = (plan, contentType) => {
    const word = fileWord(contentType);
    return plan?.linkedContentId
        ? `The other languages are on ${plan.linkedTitle}. Link this one to the same ${word}.`
        : `Create the ${word} here, or link one that's already in AmrutVaani.`;
};

/** The language options a create dialog starts on for a waiting task, leaving out the source language. */
export const targetsForWaiting = (waitingLanguages, source, options) =>
    (options || []).filter((o) => o.label !== source && (waitingLanguages || []).includes(o.label));

/** The drawer's next step for a waiting video, from what its shared folder and Source video hold. */
export const waitingVideoLine = (plan, task) => {
    if (plan?.linkedContentId) return `The other languages are on ${plan.linkedTitle}. Link this one to the same video.`;
    if (plan?.videoFiles > 0) return 'The video is in the shared folder, ready to start.';
    if (isDriveLink(task?.sourceUrl)) return 'Ready to start from the Google Drive link.';
    if (plan?.folderFiles > 0) {
        const n = plan.folderFiles;
        return `The shared folder has ${n} file${n === 1 ? '' : 's'}, but none is a video. You can start from a Google Drive link.`;
    }
    return 'Add the video to the shared folder, or start it from a Google Drive link.';
};

const START_FAILURES = {
    DRIVE_UNAVAILABLE: "Google Drive wouldn't give us this file. Check it's shared as Anyone with the link, or try later.",
    TOO_BIG: "This file is over 12 GB, so it can't be downloaded from Drive. Add it to the shared folder, or use a smaller copy.",
    STALLED: 'The download stopped or took too long. Try again, or add the file to the shared folder.',
    NOT_VIDEO: "This Drive file isn't a video. Check the link.",
    NO_ROOM: 'The server ran out of room. Try again later.',
};

/** What the drawer says about a failed Start video. startedVideo is the task detail's. */
export const startFailedLine = (startedVideo) => {
    if (!startedVideo?.fromDriveLink) return 'The video failed to process. Its file is still in the shared folder.';
    return START_FAILURES[startedVideo.startFailure] || 'The video failed to process. Start it again.';
};

/** What the drawer says while a Start video processes. */
export const videoProcessingLine = (startedVideo) =>
    startedVideo?.fromDriveLink
        ? "The video is still processing. A large Drive file can take hours. It opens in the editor once it's ready."
        : "The video is still processing. It opens in the editor once it's ready.";

export const formatFileSize = (bytes) => {
    if (!(bytes > 0)) return '';
    const gb = bytes / 1024 ** 3;
    return gb >= 1 ? `${gb.toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
};

/**
 * Start video's languages. fixed always go on: Gujarati and English, as in New video, and the
 * task's own. addable can be added, and gets no task.
 */
export const startVideoLanguages = (taskLanguages, options) => {
    const fixed = [...new Set(['Gujarati', 'English', ...(taskLanguages || [])])];
    return {
        fixed,
        addable: (options || [])
            .map(({ value, label }) => ({ value, label: String(label) }))
            .filter((o) => !fixed.includes(o.label)),
    };
};

/** What Cancel or Delete does to a waiting task's shared folder. Empty when it holds no files. */
export const planFolderLine = (plan) => {
    if (!plan || !(plan.folderFiles > 0)) return '';
    if (!plan.lastLanguage) return 'Its shared folder stays for the other languages.';
    const n = plan.folderFiles;
    return `The ${n === 1 ? 'file' : `${n} files`} in its shared folder will be removed in 30 days.`;
};

/** One line for a Complete across languages. failed: [{ label, message }]. */
export const completeResultLine = (stageName, done, failed) => {
    const failures = failed.map((f) => `${f.label}: ${f.message}`).join(' ');
    return `${stageName} completed for ${done} of ${done + failed.length} languages. ${failures}`;
};

/** "12 Nov, 14:05" for the drawer's stages, comments and History. */
export const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
